import React, { useState, useEffect, Component, lazy, Suspense } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { HarmonyLogoCompact } from '../components/HarmonyLogo';
import { TimesheetProvider, useTimesheet } from '../context/TimesheetContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../components/ui/sheet';
import {
  LayoutDashboard, CalendarClock, History, Send,
  FileBarChart2, Bell, ChevronDown, LogOut,
  AlertTriangle, RefreshCw, Settings, CalendarRange, Menu,
  PanelLeftClose, PanelLeftOpen, Loader2,
} from 'lucide-react';
import usePageMeta from '../hooks/usePageMeta';
import { t } from '../components/timesheet/i18n';
import { LoadingState } from '../components/timesheet/kit';
import { cn } from '../lib/utils';

// Code-split each top-level tab so the initial bundle is just the shell;
// each tab's bundle is fetched the first time the user opens it.
const TimesheetDashboard = lazy(() => import('../components/timesheet/TimesheetDashboard'));
const TimesheetEntry = lazy(() => import('../components/timesheet/TimesheetEntry'));
const TimesheetHistory = lazy(() => import('../components/timesheet/TimesheetHistory'));
const TimesheetSubmissions = lazy(() => import('../components/timesheet/TimesheetSubmissions'));
const TimesheetReports = lazy(() => import('../components/timesheet/TimesheetReports'));
const TimesheetNotifications = lazy(() => import('../components/timesheet/TimesheetNotifications'));
// Runn integration lives inside this sub-app under ONE top-level tab
// ("Runn Allocations") that hosts an internal sub-nav across the four
// Runn views. RunnHub owns the section-picker + lazy child loading, so
// this outer file only sees a single component.
const RunnHub = lazy(() => import('../components/timesheet/RunnHub'));
// Same pattern for admin config: one "Admin Section" tab with a sub-nav
// across Projects / Categories / Holidays / Email / Backup / Resource
// Assignments. AdminHub owns the section state + lazy child loading.
const AdminHub = lazy(() => import('../components/timesheet/AdminHub'));

class TimesheetErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('Timesheet Error:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
          <div className="w-full max-w-md rounded-xl border bg-card p-8 text-center shadow-sm">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-warning-soft">
              <AlertTriangle aria-hidden="true" className="h-6 w-6 text-warning" />
            </div>
            <h1 className="font-heading text-xl font-semibold text-foreground">Something went wrong</h1>
            <p className="mt-2 text-sm text-muted-foreground">Harmony Timesheet encountered an error.</p>
            <p className="mt-4 break-words rounded-md bg-danger-soft px-3 py-2 font-mono text-xs text-danger-strong">
              {this.state.error?.message || 'Unknown error'}
            </p>
            <Button onClick={() => window.location.reload()} className="mt-6">
              <RefreshCw aria-hidden="true" />Reload App
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Sidebar / drawer navigation. Sections are separate views, so this is a
// list of buttons with aria-current rather than a tablist. Only the desktop
// sidebar carries the tab-* test ids, so they stay unique in the DOM.
const NavList = ({ groups, activeTab, onSelect, collapsed = false, withTestIds = false }) => (
  <div className="flex flex-col gap-6">
    {groups.map((g) => (
      <div key={g.label}>
        {collapsed
          ? <div className="mx-2 mb-2 border-t" aria-hidden="true" />
          : <p className="px-3 pb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">{g.label}</p>}
        <ul className="flex flex-col gap-0.5">
          {g.items.map((it) => {
            const active = it.value === activeTab;
            const Icon = it.icon;
            const badge = it.badge > 0 ? it.badge : 0;
            return (
              <li key={it.value}>
                <button
                  type="button"
                  onClick={() => onSelect(it.value)}
                  aria-current={active ? 'page' : undefined}
                  aria-label={collapsed || badge ? `${it.label}${badge ? `, ${badge} unread` : ''}` : undefined}
                  title={collapsed ? it.label : undefined}
                  data-testid={withTestIds ? `tab-${it.value}` : `nav-${it.value}`}
                  className={cn(
                    'relative flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    collapsed && 'justify-center px-0',
                  )}
                >
                  <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
                  {!collapsed && <span className="flex-1 truncate text-left">{it.label}</span>}
                  {badge > 0 && (collapsed ? (
                    <span aria-hidden="true" className="absolute right-3 top-1.5 h-2 w-2 rounded-full bg-danger" />
                  ) : (
                    <span aria-hidden="true" className="rounded-full bg-danger-soft px-1.5 py-0.5 text-xs font-semibold leading-none text-danger-strong tabular-nums">
                      {badge > 99 ? '99+' : badge}
                    </span>
                  ))}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    ))}
  </div>
);

const TimesheetContent = () => {
  usePageMeta('Harmony Timesheet | Harmony Apps', '/favicon-timesheet.svg');

  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin, isSuperAdmin, isReviewer, role, userName, unreadCount } = useTimesheet();

  const tabFromUrl = searchParams.get('tab') || 'dashboard';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [unread, setUnread] = useState(0);

  // Sync activeTab when the URL `tab` param changes from outside the tab bar
  // (e.g. Dashboard's catch-up deep-link sets tab=entry&date=…). Without
  // this, the URL updates but the visible tab stays on whatever was first
  // rendered, so the user sees "URL says entry, screen says dashboard".
  useEffect(() => {
    if (tabFromUrl !== activeTab) setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  // Poll unread notifications every 30s
  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      const c = await unreadCount();
      if (alive) setUnread(c);
    };
    refresh();
    const t = setInterval(refresh, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [unreadCount]);

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', newTab);
    setSearchParams(newParams, { replace: true });
  };

  const handleLogout = () => { window.close(); };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Tab visibility based on role.
  // Members do NOT see Submissions / Assignments / Projects / Categories.
  // Reviewers (team_lead / manager) see Submissions only.
  // Admins (super_admin / hr_admin / timesheet_role=admin) see everything,
  // including Resource Assignments (where roles + manager/team-lead/project
  // assignments are managed).
  const showSubmissionsTab = isAdmin || isReviewer;
  const showSubmissionsReview = isAdmin || isReviewer;
  // Admin Section hub gates every child section (Projects, Categories,
  // Holidays, Email, Backup, Resource Assignments) at the outer tab.
  // All six are admin-only today; if that ever splits (e.g. ts_admin
  // gets Email but not Backup) it belongs inside AdminHub, not here.
  const showAdmin = isAdmin;
  // Runn tab shows up for anyone who can use ANY of its inner sections
  // (admin has all four; TL/Manager get Import + Report). RunnHub
  // gates People/Projects internally.
  const showRunn = isAdmin || isReviewer;

  // Sidebar groups. Labels match the old tab names so the user guides
  // (docs/timesheet/USER_GUIDE_*.md) still read correctly.
  const navGroups = [
    {
      label: 'My work',
      items: [
        { value: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { value: 'entry', label: 'Timesheet Entry', icon: CalendarClock },
        { value: 'history', label: 'History', icon: History },
        { value: 'notifications', label: 'Notifications', icon: Bell, badge: unread },
      ],
    },
    {
      label: showSubmissionsTab || showRunn ? 'Team' : 'Insights',
      items: [
        showSubmissionsTab && { value: 'submissions', label: 'Submissions', icon: Send },
        { value: 'reports', label: 'Reports', icon: FileBarChart2 },
        showRunn && { value: 'runn', label: 'Runn Allocations', icon: CalendarRange },
      ].filter(Boolean),
    },
    showAdmin && {
      label: 'Administration',
      items: [{ value: 'admin', label: 'Admin Section', icon: Settings }],
    },
  ].filter(Boolean);
  const activeLabel = navGroups.flatMap((g) => g.items).find((i) => i.value === activeTab)?.label || 'Dashboard';

  // Collapsible desktop sidebar; remembered per browser. Storage can throw
  // (private mode, blocked site data), so every access is guarded.
  const [collapsed, setCollapsed] = useState(() => {
    try { return window.localStorage.getItem('ts.sidebar.collapsed') === '1'; } catch (_) { return false; }
  });
  useEffect(() => {
    try { window.localStorage.setItem('ts.sidebar.collapsed', collapsed ? '1' : '0'); } catch (_) { /* ignore */ }
  }, [collapsed]);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background" data-testid="timesheet-app">
      <a
        href="#ts-main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[60] focus:rounded-md focus:bg-card focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:shadow-md focus:outline-none focus:ring-2 focus:ring-ring"
      >
        Skip to content
      </a>

      {/* Desktop sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 hidden flex-col border-r bg-card transition-[width] duration-200 lg:flex',
          collapsed ? 'w-[72px]' : 'w-64',
        )}
      >
        <div className={cn('flex h-16 shrink-0 items-center gap-3 border-b px-4', collapsed && 'justify-center px-0')}>
          <HarmonyLogoCompact size={32} />
          {!collapsed && (
            <div className="min-w-0">
              <p className="truncate font-heading text-base font-semibold leading-tight text-foreground">{t('app.title', 'Harmony Timesheet')}</p>
              <p className="truncate text-xs text-muted-foreground">{t('app.shell', 'Harmony Apps')}</p>
            </div>
          )}
        </div>
        <nav aria-label="Timesheet sections" className="flex-1 overflow-y-auto px-3 py-4">
          <NavList groups={navGroups} activeTab={activeTab} onSelect={handleTabChange} collapsed={collapsed} withTestIds />
        </nav>
        <div className="shrink-0 border-t p-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={cn('w-full text-muted-foreground', collapsed ? 'justify-center px-0' : 'justify-start')}
            data-testid="sidebar-toggle"
          >
            {collapsed ? <PanelLeftOpen aria-hidden="true" /> : <PanelLeftClose aria-hidden="true" />}
            {!collapsed && 'Collapse'}
          </Button>
        </div>
      </aside>

      {/* Mobile navigation drawer */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="flex w-72 flex-col gap-0 p-0">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SheetDescription className="sr-only">Choose a Harmony Timesheet section</SheetDescription>
          <div className="flex h-16 shrink-0 items-center gap-3 border-b px-4">
            <HarmonyLogoCompact size={32} />
            <div className="min-w-0">
              <p className="truncate font-heading text-base font-semibold leading-tight text-foreground">{t('app.title', 'Harmony Timesheet')}</p>
              <p className="truncate text-xs text-muted-foreground">{t('app.shell', 'Harmony Apps')}</p>
            </div>
          </div>
          <nav aria-label="Timesheet sections" className="flex-1 overflow-y-auto px-3 py-4">
            <NavList
              groups={navGroups}
              activeTab={activeTab}
              onSelect={(v) => { handleTabChange(v); setMobileNavOpen(false); }}
            />
          </nav>
        </SheetContent>
      </Sheet>

      <div className={cn('flex min-h-screen flex-col transition-[padding] duration-200', collapsed ? 'lg:pl-[72px]' : 'lg:pl-64')}>
        <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-3 border-b bg-card px-4 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            size="icon"
            className="-ml-2 lg:hidden"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open navigation"
            data-testid="mobile-nav-open"
          >
            <Menu aria-hidden="true" />
          </Button>
          <div className="flex min-w-0 items-center gap-2 lg:hidden">
            <HarmonyLogoCompact size={28} />
            <span className="truncate font-heading font-semibold text-foreground">{t('app.title', 'Harmony Timesheet')}</span>
          </div>
          <p className="hidden min-w-0 truncate text-sm text-muted-foreground lg:block">
            Timesheet<span aria-hidden="true" className="mx-2 text-border">/</span>
            <span className="font-medium text-foreground">{activeLabel}</span>
          </p>

          <div className="ml-auto flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              onClick={() => handleTabChange('notifications')}
              aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
              data-testid="header-notifications"
            >
              <Bell aria-hidden="true" />
              {unread > 0 && (
                <span aria-hidden="true" className="absolute right-1 top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-danger-strong px-1 text-xs font-semibold leading-none text-white tabular-nums">
                  {unread > 99 ? '99+' : unread}
                </span>
              )}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 px-2" data-testid="user-menu">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary text-sm text-primary-foreground">
                      {getInitials(userName)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden max-w-[12rem] truncate text-sm font-medium sm:block">{userName}</span>
                  <ChevronDown aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <p className="truncate text-sm font-medium">{userName}</p>
                  <p className="text-xs font-normal capitalize text-muted-foreground">{(role || '').replace('_', ' ')}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Close App
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main id="ts-main" tabIndex={-1} className="mx-auto w-full max-w-[1440px] flex-1 px-4 py-6 focus:outline-none sm:px-6 lg:px-8 lg:py-8">
          <Suspense fallback={<LoadingState data-testid="tab-loading" />}>
          {activeTab === 'dashboard' && <TimesheetDashboard onJumpToTab={handleTabChange} />}
          {activeTab === 'entry' && <TimesheetEntry />}
          {activeTab === 'history' && <TimesheetHistory />}
          {activeTab === 'submissions' && showSubmissionsTab && <TimesheetSubmissions canReview={showSubmissionsReview} />}
          {activeTab === 'reports' && <TimesheetReports />}
          {/* Single Admin Section tab hosts six admin config sub-views.
              AdminHub reads its initial section from ?section= and
              reports section changes back so we can keep the URL in
              sync for deep-links. */}
          {activeTab === 'admin' && showAdmin && (
            <AdminHub
              initialSection={searchParams.get('section') || undefined}
              onSectionChange={(section) => {
                const newParams = new URLSearchParams(searchParams);
                newParams.set('tab', 'admin');
                if (section && section !== 'projects') newParams.set('section', section);
                else newParams.delete('section');
                setSearchParams(newParams, { replace: true });
              }}
            />
          )}
          {/* One Runn tab, four sub-sections. RunnHub reads its
              initial section from ?section= and reports section
              changes back so we can keep the URL in sync for
              deep-links. A child hand-off also passes params (e.g.
              { import_ids } from Import → Report); those are folded
              into the URL and ?section= is named even for the default
              'report', because RunnHub switches by following it. */}
          {activeTab === 'runn' && showRunn && (
            <RunnHub
              isAdmin={isAdmin}
              initialSection={searchParams.get('section') || undefined}
              onSectionChange={(section, params) => {
                const newParams = new URLSearchParams(searchParams);
                newParams.set('tab', 'runn');
                if (section && (section !== 'report' || params)) newParams.set('section', section);
                else newParams.delete('section');
                Object.entries(params || {}).forEach(([k, v]) => {
                  if (v == null || v === '') newParams.delete(k);
                  else newParams.set(k, v);
                });
                setSearchParams(newParams, { replace: true });
              }}
            />
          )}
          {activeTab === 'notifications' && (
            <TimesheetNotifications
              onUnreadChange={setUnread}
              onNavigate={(tab, params) => {
                // Notification click → jump to the relevant tab AND fold any
                // params (e.g. focusDate) into the URL so the destination tab
                // can pre-filter / scroll on mount.
                const newParams = new URLSearchParams(searchParams);
                newParams.set('tab', tab);
                Object.entries(params || {}).forEach(([k, v]) => {
                  if (v == null || v === '') newParams.delete(k);
                  else newParams.set(k, v);
                });
                setSearchParams(newParams, { replace: true });
                setActiveTab(tab);
              }}
            />
          )}
          </Suspense>
        </main>
      </div>
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Best-effort JWT payload decode. The bootstrap uses it purely as a
// fallback when /auth/me fails but /auth/refresh succeeded — we can still
// render the app with role + username from the token claims.
const _peekJwt = (jwt) => {
  try {
    const [, payload] = (jwt || '').split('.');
    if (!payload) return null;
    const base = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base + '='.repeat((4 - base.length % 4) % 4)));
  } catch (_) { return null; }
};

const TimesheetApp = () => {
  // Bootstrap the sub-app from the shell's HttpOnly refresh cookie instead
  // of a `?token=<JWT>` query param. Same origin, same browser profile →
  // the cookie set by /api/auth/login rides along automatically. Fresh
  // access token stays in memory only, never touches URL/history/logs.
  const [bootState, setBootState] = useState({ status: 'loading' });
  const navigate = useNavigate();

  // Scoped theme tokens (see body.ts-theme in index.css). On <body> rather
  // than a wrapper so dialogs, menus and toasts — which portal to <body> —
  // pick them up too. Removed on unmount so other apps are unaffected.
  useEffect(() => {
    document.body.classList.add('ts-theme');
    return () => { document.body.classList.remove('ts-theme'); };
  }, []);

  // Strip token/user/role from the URL for bookmarks saved by the previous
  // build. Preserve other params (?tab=entry&date=…): the catch-up deep
  // links rely on them. Go through the router, rebuilt from the address
  // bar: BrowserRouter never sees a raw history.replaceState, so its copy
  // of the URL kept the token and the next setSearchParams built from that
  // copy (any tab click) wrote it back. Runs on mount, ahead of the auth
  // round trip, so the router holds the clean URL before any tab mounts
  // (Entry clears ?date= from the router's copy).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let dirty = false;
    for (const k of ['token', 'user', 'role']) {
      if (params.has(k)) { params.delete(k); dirty = true; }
    }
    if (dirty) navigate({ search: params.toString(), hash: window.location.hash }, { replace: true });
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 1. Cookie-based refresh — HttpOnly cookie is sent because of
        // withCredentials. No Authorization header needed here.
        const refresh = await axios.post(
          `${BACKEND_URL}/api/auth/refresh`,
          {},
          { withCredentials: true },
        );
        const token = refresh.data?.token;
        if (!token) throw new Error('No token in refresh response');

        // 2. Fetch profile so we can render the display name & authoritative
        // role in the header. JWT claims are used only if /auth/me errors.
        let userName = 'User';
        let role = 'viewer';
        try {
          const me = await axios.get(
            `${BACKEND_URL}/api/auth/me`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          userName = me.data?.full_name || me.data?.username || userName;
          role = me.data?.role || role;
        } catch (_) {
          const claims = _peekJwt(token);
          if (claims?.username) userName = claims.username;
          if (claims?.role) role = claims.role;
        }

        if (!cancelled) setBootState({ status: 'ready', token, userName, role });
      } catch (e) {
        if (!cancelled) setBootState({ status: 'unauthenticated' });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (bootState.status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div role="status" aria-live="polite" className="flex flex-col items-center gap-4 text-center">
          <HarmonyLogoCompact size={40} />
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
            Loading Harmony Timesheet…
          </p>
        </div>
      </div>
    );
  }

  if (bootState.status === 'unauthenticated') {
    const loginUrl = `${window.location.origin}/login`;
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="w-full max-w-md rounded-xl border bg-card p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <HarmonyLogoCompact size={28} />
          </div>
          <h1 className="font-heading text-xl font-semibold text-foreground">Session expired</h1>
          <p className="mt-2 text-sm text-muted-foreground">Please sign in to the Harmony Apps dashboard, then launch Timesheet again.</p>
          <Button className="mt-6" onClick={() => { window.location.href = loginUrl; }}>Go to sign-in</Button>
        </div>
      </div>
    );
  }

  // Toasts render in the app-wide Toaster (App.js), which wraps every route.
  // No local one: two mounted Toasters show every toast twice.
  return (
    <TimesheetErrorBoundary>
      <TimesheetProvider token={bootState.token} role={bootState.role} userName={bootState.userName}>
        <TimesheetContent />
      </TimesheetProvider>
    </TimesheetErrorBoundary>
  );
};

export default TimesheetApp;
